/**
 * useNotification Hook
 *
 * This custom hook provides a reusable function for displaying notifications
 * in a multi-language environment using notistack and Material-UI.
 *
 * @returns A function to show notifications with customizable type, title, and message.
 *
 * @example
 * const showNotification = useNotification();
 * showNotification('success', 'profile.changePasswordNotification');
 */
import React from 'react';
import { useTranslation } from 'react-i18next';

import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, Typography } from '@mui/material';
import { OptionsObject, SnackbarKey, closeSnackbar, enqueueSnackbar } from 'notistack';

type NotificationType = 'default' | 'error' | 'success' | 'warning' | 'info';

interface NotificationOptions extends OptionsObject {
	dismissible?: boolean;
	withoutTitle?: boolean;
}

const useNotification = () => {
	const { t } = useTranslation();

	const showNotification = (
		type: NotificationType,
		label: string,
		options: NotificationOptions = {},
	): SnackbarKey => {
		const key: SnackbarKey = enqueueSnackbar(
			<Box>
				{!options.withoutTitle && (
					<Typography variant="subtitle1">{t(`shared.notifications.${type}`)}</Typography>
				)}
				<Typography variant="body2">{label}</Typography>
			</Box>,
			{
				variant: type,
				hideIconVariant: true,
				autoHideDuration: 2000,
				action: options.dismissible
					? (snackbarKey) => (
							<IconButton size="small" onClick={() => closeSnackbar(snackbarKey)}>
								<CloseIcon sx={{ color: 'white' }} />
							</IconButton>
						)
					: undefined,
				...options,
			},
		);

		return key;
	};

	showNotification.close = (key: SnackbarKey) => {
		closeSnackbar(key);
	};

	return showNotification;
};

export default useNotification;
